import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/client";

import { TalentPickGraphData } from "@tvg/ts-types/Race";
import { AccountRolesEnum } from "@tvg/ts-types/Account";
import { get } from "lodash";
import { UseTalentPicksProps } from "../types";
import ApolloOptions, {
  getTalentPicksWithVisualSelections,
  getUniqueTalents
} from "../graphql/options.graph";
import { GET_GRAPH_TALENT_PICKS } from "../graphql/queries/talentPicksQuery";
import useTalentPicksSubscription from "./useTalentPicksSubscription";
import { transformTalentPickToGraph } from "../utils/talentPicks";
import useGetTalentPicksList from "./useGetTalentPicksList";
import { userRoles } from "../redux/selectors";

const useTalentPicks = ({
  skip,
  trackCode,
  raceNumber,
  wagerProfile,
  races,
  useCache = false,
  canCreateTalentPicks = false
}: UseTalentPicksProps) => {
  const accountRoles = useSelector(userRoles);

  const { talentPicks: apiData, refetchTalentPicksList } =
    useGetTalentPicksList(
      !canCreateTalentPicks,
      accountRoles.includes(AccountRolesEnum.TALENT)
    );

  const { loading, subscribeToMore, data } = useQuery(
    GET_GRAPH_TALENT_PICKS,
    ApolloOptions.options({
      skip,
      wagerProfile,
      useCache
    })
  );

  useEffect(() => {
    if (canCreateTalentPicks) {
      refetchTalentPicksList();
    }
  }, [canCreateTalentPicks]);

  const talentPicksList: Array<TalentPickGraphData> = useMemo(() => {
    const graphList = get(data, "talentPicksList", []) as TalentPickGraphData[];

    const transformedList = canCreateTalentPicks
      ? apiData.map((tp) => transformTalentPickToGraph(tp))
      : [];

    const mergedMap = new Map<string, TalentPickGraphData>();
    graphList.forEach((item) => {
      mergedMap.set(item.id, item);
    });
    transformedList.forEach((item) => {
      mergedMap.set(item.id, item);
    });

    return Array.from(mergedMap.values()).filter((talentPick) => {
      if (
        !talentPick.selections ||
        !talentPick.wagerType ||
        !talentPick.favorites
      ) {
        return false;
      }
      if (raceNumber && trackCode) {
        return (
          talentPick.race?.number === raceNumber &&
          talentPick.race?.track?.code === trackCode
        );
      }
      return true;
    });
  }, [data, apiData, raceNumber, trackCode, canCreateTalentPicks]);

  const talentPicks = useMemo(() => {
    const talentPicksWithVisualSelections = getTalentPicksWithVisualSelections(
      talentPicksList,
      races
    );

    return {
      talentPicks: talentPicksWithVisualSelections,
      talentsList: getUniqueTalents(talentPicksWithVisualSelections)
    };
  }, [JSON.stringify(talentPicksList), races]);

  useTalentPicksSubscription(subscribeToMore, wagerProfile, useCache);

  return useMemo(
    () => ({
      loading,
      talentPicks,
      refetchTalentPicksList
    }),
    [loading, talentPicks, refetchTalentPicksList]
  );
};

export default useTalentPicks;
